import { useEffect, useRef, useState } from 'react';
import { SmilePlus, XCircle } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { OPERATOR_REACTION_EMOJIS } from '@/lib/gupshup-client';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { QuotedMessage } from './quoted-message';
import type { Message } from './types';

/** Touch hold that opens the reaction picker, matching WhatsApp's long-press. */
const LONG_PRESS_MS = 500;

function formatMessageTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    if (isValid(date)) {
      return format(date, 'HH:mm');
    }
    return '';
  } catch {
    return '';
  }
}

/**
 * Bubble frame: alignment, footer (time + type + status ticks), failed row, reaction badge.
 *
 * With `onReact`, an incoming bubble also gets the operator's reaction picker: a hover button on
 * desktop, a long-press on touch. Tapping the emoji we already reacted with passes `''` (remove).
 *
 * A message that quotes another (`message.replyTo`) opens with the quote strip; `quoted` is the
 * resolved message, or null when it is not among the loaded pages.
 */
export function MessageBubble({
  message,
  children,
  onReact,
  quoted,
}: {
  message: Message;
  children: React.ReactNode;
  onReact?: (emoji: string) => void;
  quoted?: Message | null;
}) {
  const { t } = useTranslation('conversations');
  const [pickerOpen, setPickerOpen] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  const reactionEmojis = [message.reactions?.customer?.emoji, message.reactions?.business?.emoji].filter(Boolean);
  const canReact = !!onReact && message.direction === 'inbound';
  const businessEmoji = message.reactions?.business?.emoji;

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  useEffect(() => cancelLongPress, []);

  const startLongPress = () => {
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      setPickerOpen(true);
    }, LONG_PRESS_MS);
  };

  const handlePick = (emoji: string) => {
    setPickerOpen(false);
    onReact?.(emoji === businessEmoji ? '' : emoji);
  };

  const bubble = (
    <div
      className={cn(
        'max-w-[70%] rounded-lg px-3 py-2 relative shadow-sm',
        message.direction === 'outbound'
          ? 'bg-chat-bubble-out text-chat-text rounded-br-none'
          : 'bg-chat-bubble-in text-chat-text rounded-bl-none',
        reactionEmojis.length > 0 && 'mb-2',
      )}
    >
      {message.replyTo && <QuotedMessage quoted={quoted ?? null} inOutbound={message.direction === 'outbound'} />}
      {children}

      <div className="flex items-center gap-1.5 mt-1">
        <span className="text-[11px] text-chat-text-muted">{formatMessageTime(message.createdAt)}</span>

        {message.messageType && (
          <span className="text-[11px] text-chat-text-muted opacity-60">
            ·{' '}
            {t(`messageView.messageTypes.${message.messageType}`, {
              defaultValue: message.messageType,
            })}
          </span>
        )}

        {message.direction === 'outbound' && message.status && (
          <>
            {message.status === 'failed' ? (
              <XCircle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
            ) : message.status === 'read' ? (
              <span className="text-xs text-chat-receipt-read">✓✓</span>
            ) : message.status === 'delivered' ? (
              <span className="text-xs text-chat-text-muted">✓✓</span>
            ) : message.status === 'sent' || message.status === 'pending' ? (
              <span className="text-xs text-chat-text-muted">✓</span>
            ) : null}
          </>
        )}
      </div>

      {message.direction === 'outbound' && message.status === 'failed' && (
        <div className="mt-1 text-[11px] text-red-500 dark:text-red-400">
          <span className="flex items-center gap-1">
            {t('messageView.notDelivered')}
            {message.deliveryError?.code && (
              <span className="opacity-80">
                · {t('messageView.deliveryErrorCode', { code: message.deliveryError.code })}
              </span>
            )}
          </span>
          {message.deliveryError?.reason && <p className="opacity-80 break-words">{message.deliveryError.reason}</p>}
        </div>
      )}

      {reactionEmojis.length > 0 && (
        <div className="absolute -bottom-2 -right-2 bg-chat-panel rounded-full px-1.5 py-0.5 text-sm shadow-sm border">
          {reactionEmojis.join(' ')}
        </div>
      )}
    </div>
  );

  if (!canReact) {
    return (
      <div className={cn('flex mb-2', message.direction === 'outbound' ? 'justify-end' : 'justify-start')}>
        {bubble}
      </div>
    );
  }

  return (
    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
      <div
        className="group flex items-center gap-2 mb-2 justify-start"
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchMove={cancelLongPress}
        onTouchCancel={cancelLongPress}
      >
        <PopoverAnchor asChild>{bubble}</PopoverAnchor>
        {/* Plain button, not PopoverTrigger: next to a PopoverAnchor, Radix remounts the trigger once
            the custom anchor registers and leaves the popper measuring the detached node, which
            pins the picker to the viewport's top-left corner. */}
        <button
          ref={openButtonRef}
          type="button"
          aria-label={t('messageView.reactions.open')}
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
          data-state={pickerOpen ? 'open' : 'closed'}
          onClick={() => setPickerOpen((open) => !open)}
          className="sr-only pointer-fine:not-sr-only pointer-fine:inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-chat-text-muted opacity-0 transition-opacity hover:bg-chat-hover focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <SmilePlus className="h-4 w-4" />
        </button>
      </div>
      <PopoverContent
        side="top"
        align="start"
        onInteractOutside={(event) => {
          // Let the button's own toggle close it, instead of dismiss-then-reopen.
          if (openButtonRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
        className="w-auto rounded-full border-chat-border bg-chat-elevated p-1 text-chat-text"
      >
        <div role="group" aria-label={t('messageView.reactions.pickerLabel')} className="flex gap-0.5">
          {OPERATOR_REACTION_EMOJIS.map((emoji) => {
            const selected = emoji === businessEmoji;
            return (
              <button
                key={emoji}
                type="button"
                aria-pressed={selected}
                aria-label={
                  selected
                    ? t('messageView.reactions.remove', { emoji })
                    : t('messageView.reactions.reactWith', { emoji })
                }
                onClick={() => handlePick(emoji)}
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full text-xl transition-colors hover:bg-chat-hover',
                  selected && 'bg-chat-accent/20 hover:bg-chat-accent/30',
                )}
              >
                {emoji}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
